/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { isEscapeKey } from '@common/tech-shared';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';
import { TooltipDirective } from '../../tooltip/tooltip.directive';

const dropdownButtonVariants = cva('block w-fit empty:hidden', {
  variants: {
    variant: {
      neutral:
        'hover:bg-neutral-100 focus:bg-neutral-200 focus:outline-hidden dark:hover:bg-neutral-700 dark:focus:bg-neutral-600',
      'dark-neutral':
        'hover:bg-neutral-300 focus:bg-neutral-300 focus:outline-hidden dark:hover:bg-neutral-700 dark:focus:bg-neutral-600',
      'user-profile': 'ring ring-primary focus:ring-2 focus:outline-none',
    },
    rounded: {
      large: 'rounded-lg',
      full: 'rounded-full',
    },
    size: {
      'extra-small': 'p-0.5',
      small: 'p-1',
      medium: 'p-2',
    },
    divided: {
      none: '',
      grayborder: 'divide-y divide-grayborder',
    },
  },
  defaultVariants: { variant: 'neutral', rounded: 'full', size: 'medium' },
});
const dropdownVariants = cva(
  'animate-fade-in bg-background-100 ring-grayborder dark:ring-background-200 absolute z-50 mt-2 max-w-[calc(100vw-1rem)] min-w-44 rounded-lg ring-1 drop-shadow-lg focus:outline-hidden',
  {
    variants: {
      divided: {
        none: '',
        grayborder: 'divide-y divide-grayborder',
      },
    },
    defaultVariants: { divided: 'none' },
  },
);

type DropdownButtonVariants = VariantProps<typeof dropdownButtonVariants>;
type DropdownVariants = VariantProps<typeof dropdownVariants>;

@Component({
  selector: 'ods-dropdown-menu',

  imports: [CommonModule, CdkTrapFocus, TooltipDirective],
  host: { class: 'block relative w-fit' },
  template: `<button
      #button
      [tooltip]="buttonTooltip"
      tooltipAriaType="aria-label"
      (click)="handleButtonClick($event)"
      [ngClass]="dropdownButtonVariants({ variant: buttonVariant, rounded: buttonRounded, size: buttonSize })"
      [attr.aria-expanded]="isOpen"
      [attr.aria-controls]="isOpen ? dropdownListId : null"
      [attr.data-test-id]="buttonTestId"
      aria-haspopup="true"
    >
      <ng-content select="[button-content]" />
    </button>
    @if (isOpen) {
      <div
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        role="dialog"
        aria-modal="true"
        [aria-label]="buttonTooltip ?? dialogLabel"
      >
        <ul #popupList [id]="dropdownListId" [style.left.px]="popupLeft" tabindex="-1" [class]="dropdownVariants({ divided })">
          <ng-content />
        </ul>
      </div>
    }`,
})
export class DropdownMenuComponent {
  readonly _cdr = inject(ChangeDetectorRef);

  @Input() alignTo: 'left' | 'right' = 'left';
  @Input() buttonVariant: DropdownButtonVariants['variant'];
  @Input() buttonRounded: DropdownButtonVariants['rounded'];
  @Input() buttonSize: DropdownButtonVariants['size'];
  @Input() divided: DropdownVariants['divided'];
  @Input() buttonTooltip: string;
  @Input() dialogLabel: string = null;
  @Input() buttonTestId: string = 'dropdown-button';

  @Output() opened: EventEmitter<void> = new EventEmitter<void>();
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  public isOpen: boolean = false;
  public popupLeft: number = 0;
  public readonly dropdownListId: string = uniqueId('dropdown-list-');

  private static readonly VIEWPORT_MARGIN = 8;

  public readonly dropdownButtonVariants = dropdownButtonVariants;
  public readonly dropdownVariants = dropdownVariants;

  readonly _ref: ElementRef = inject(ElementRef);
  @ViewChild('button') buttonRef: ElementRef<HTMLButtonElement>;
  @ViewChild('popupList') popupListRef: ElementRef<HTMLUListElement>;

  @HostListener('document:keydown', ['$event'])
  onKeydownHandler(e: KeyboardEvent): void {
    if (!this.isOpen) return;

    if (isEscapeKey(e)) this.close();
  }

  @HostListener('document:click', ['$event'])
  onClickHandler(e: MouseEvent): void {
    if (!this.isOpen) return;

    if (!this._ref.nativeElement.contains(e.target as HTMLElement)) this.close();
  }

  handleButtonClick(e: MouseEvent): void {
    e.stopPropagation();
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.isOpen = true;
    this.opened.emit();
    this._cdr.detectChanges();
    this._calculatePosition();
    this.popupListRef.nativeElement.focus();
  }

  close(focusOnButton: boolean = true): void {
    this.isOpen = false;
    this.closed.emit();
    if (focusOnButton) this.buttonRef.nativeElement.focus();
  }

  _calculatePosition(): void {
    const buttonRect: DOMRect = this.buttonRef.nativeElement.getBoundingClientRect();
    const popupWidth: number = this.popupListRef.nativeElement.getBoundingClientRect().width;
    const margin: number = DropdownMenuComponent.VIEWPORT_MARGIN;

    const preferredLeft: number = this.alignTo === 'left' ? buttonRect.right - popupWidth : buttonRect.left;
    const maxLeft: number = Math.max(document.documentElement.clientWidth - popupWidth - margin, margin);

    this.popupLeft = Math.min(Math.max(preferredLeft, margin), maxLeft) - buttonRect.left;
  }
}
